import { PaymentMethod } from '@wix/cashier-common/dist/src/enums/payments/PaymentMethod';
import { IMandatoryField } from '@wix/cashier-core/dist/src/services/MandatoryFields/types';
import isEqual from 'lodash/isEqual';
import debounce from 'lodash/debounce';
import React from 'react';
import {
  isMFieldValueExists,
  UIMFieldMeta,
  transformToUIMFields,
  sortMFieldsByCountryFactory,
} from '../../utils/mandatoryFieldsMeta';
import { DeviceType } from '../../types/DeviceType';
import { MFieldName, MFieldValuePairs } from '../../types/MandatoryFields';
import { Theme } from '../../types/Theme';
import { underscoreToCamelCase } from '../../utils/underscoreToCamelCase';
import { MFieldsState, MFieldState, PaymentDetailsAPI } from './types';
import { createMFieldState } from './utils';
import { PaymentDetailsView } from './view';
import { MetadataService } from '@wix/cashier-core/dist/src/services/Metadata';
import { MFieldType } from '../../types/MFieldType';
import { alpha3ToAlpha2 } from '@wix/cashier-common/dist/src/utils/countryCodes';
import { FetchAddressV2Response as Address } from '@wix/cashier-core/dist/src/services/Metadata/types';
import { generateGUID } from '@wix/cashier-common/dist/src/utils/guid/generateGUID';
import { DEBOUNCE_TIME } from './constants';
import { patchMetadataServiceBaseURL } from './Field/utils';
import { getBiLogger } from '../../utils/biLogger';

export interface PaymentDetailsControllerProps {
  host?: string;
  theme: Theme;
  deviceType: DeviceType;
  countryCode?: string;
  locale: string;
  paymentMethod: PaymentMethod;
  orderId: string;
  mFieldsMeta: IMandatoryField[];
  mandatoryFields?: MFieldValuePairs;
  mandatoryFieldsPrefilled?: MFieldValuePairs;
  isCollapsed?: boolean;
  shouldApplySiteStyles?: boolean;

  onCountryChange(countryCode: string): void;
  onIsValidChanged(isValid: boolean): void;
  onApiInit(api: PaymentDetailsAPI): void;
}

interface PaymentDetailsControllerState {
  allMFieldsState?: MFieldsState; // accumulated state that stores fields state (from previous PMs/countries as well)
  countryCode?: string;
}

export class PaymentDetailsController extends React.Component<
  PaymentDetailsControllerProps,
  PaymentDetailsControllerState
> {
  activeFetchAddressRequestId: string = null;
  fieldsRefs: {
    [mFieldName in MFieldName]?: HTMLInputElement;
  } = {};

  state: PaymentDetailsControllerState = {
    countryCode: this.props.countryCode,
    allMFieldsState: {},
  };

  componentDidMount() {
    this.returnApi();
    this.updateMFieldsState().then(() => {
      // need to wait for state to apply
      this.props.onIsValidChanged(this.isFormValid());
    });
  }

  componentDidUpdate(
    prevProps: PaymentDetailsControllerProps,
    prevState: PaymentDetailsControllerState
  ) {
    if (
      prevProps.countryCode !== this.props.countryCode &&
      this.props.countryCode !== this.state.countryCode
    ) {
      this.updateCountry(this.props.countryCode);
    } else {
      const curCountryCode =
        this.state.allMFieldsState?.countryCode?.value ||
        this.props.countryCode;
      if (this.state.countryCode !== curCountryCode) {
        this.updateCountry(curCountryCode);
      }
    }

    if (this.isFormValid() !== this.isFormValid(prevState, prevProps)) {
      this.props.onIsValidChanged(this.isFormValid());
    }

    if (!isEqual(this.props.mFieldsMeta, prevProps.mFieldsMeta)) {
      this.updateMFieldsState();
    }
  }

  returnApi() {
    this.props.onApiInit({
      validate: this.validate,
      invalidateField: this.invalidateField,
      getInvalidFields: this.getInvalidMFields,
      getFields: this.getMFields,
    } as PaymentDetailsAPI);
  }

  updateCountry = (countryCode: string) => {
    const { allMFieldsState } = this.state;

    this.setState({ countryCode });

    this.updateFieldState('countryCode', {
      value: countryCode,
      isValid: true,
    });

    if (allMFieldsState.state) {
      this.updateFieldState('state', {
        value: undefined,
        isValid: false,
        touched: false,
      });
    }
  };

  updateFieldState = (
    mFieldName: MFieldName,
    mFieldState: Partial<MFieldState>
  ) => {
    this.setState((state: PaymentDetailsControllerState) => ({
      allMFieldsState: {
        ...state.allMFieldsState,
        [mFieldName]: {
          ...state.allMFieldsState[mFieldName],
          touched: true,
          ...mFieldState,
          name: mFieldName,
        },
      },
    }));
  };

  getActiveUIMFieldsMeta(
    props: PaymentDetailsControllerProps = this.props
  ): UIMFieldMeta[] {
    return transformToUIMFields(props.mFieldsMeta);
  }

  getActiveMFieldsNames(
    props: PaymentDetailsControllerProps = this.props
  ): MFieldName[] {
    return this.getActiveUIMFieldsMeta(props).map(({ name }) => name);
  }

  updateMFieldsState = async () => {
    // keep old refs to fields that are still mandatory and filter out non-relevant
    // new fields will get their refs inited on next render
    this.fieldsRefs = this.getActiveMFieldsNames().reduce((acc, mFieldName) => {
      if (this.fieldsRefs[mFieldName]) {
        acc[mFieldName] = this.fieldsRefs[mFieldName];
      }
      return acc;
    }, {});

    await new Promise((resolve) => {
      this.setState(
        (state: PaymentDetailsControllerState) => ({
          allMFieldsState: this.getActiveMFieldsNames().reduce(
            (allMFieldsState, mFieldName) => {
              if (!allMFieldsState.hasOwnProperty(mFieldName)) {
                allMFieldsState[mFieldName] = createMFieldState(mFieldName, {
                  value:
                    (mFieldName === 'countryCode' && state.countryCode) ||
                    this.props.mandatoryFieldsPrefilled?.[mFieldName],
                });
              }
              return allMFieldsState;
            },
            { ...state.allMFieldsState }
          ),
        }),
        () => {
          resolve();
        }
      );
    });
  };

  isMFieldProvidedAndValid = (
    mfName: MFieldName,
    props: PaymentDetailsControllerProps = this.props
  ): boolean =>
    Object.entries(props.mandatoryFields || {})
      .filter(([, value]) => isMFieldValueExists(value))
      .map(([key]) => key)
      .map(underscoreToCamelCase)
      .includes(mfName);

  markActiveMFieldsAsTouched = () => {
    this.setState((state: PaymentDetailsControllerState) => ({
      allMFieldsState: Object.values(state.allMFieldsState).reduce(
        (acc, fieldState) => {
          if (
            this.getActiveMFieldsNames().some(
              (name) => name === fieldState.name
            )
          ) {
            acc[fieldState.name] = {
              ...fieldState,
              touched: true,
            };
          }
          return acc;
        },
        { ...state.allMFieldsState }
      ),
    }));
  };

  getInvalidMFields = (): MFieldName[] =>
    this.getActiveMFieldsNames()
      .filter(
        (mFieldName) =>
          !this.isMFieldProvidedAndValid(mFieldName) &&
          !this.state.allMFieldsState[mFieldName].isValid
      )
      .sort(sortMFieldsByCountryFactory(this.state.countryCode)); // sorted to be aligned with UI

  isFormValid = (
    state: PaymentDetailsControllerState = this.state,
    props: PaymentDetailsControllerProps = this.props
  ): boolean =>
    this.getActiveMFieldsNames(props).every(
      (mFieldName) =>
        this.isMFieldProvidedAndValid(mFieldName, props) ||
        state.allMFieldsState[mFieldName]?.isValid
    );

  validate = (): {
    isValid: boolean;
    invalidFields: MFieldName[];
  } => {
    this.markActiveMFieldsAsTouched();

    return {
      isValid: this.isFormValid(),
      invalidFields: this.getInvalidMFields(),
    };
  };

  invalidateField = (mFieldName: MFieldName) => {
    if (
      !this.getActiveMFieldsNames().some((name) => name === mFieldName) ||
      !this.state.allMFieldsState[mFieldName]
    ) {
      return;
    }

    this.fieldsRefs[mFieldName]?.focus();
    this.setState((state: PaymentDetailsControllerState) => ({
      allMFieldsState: {
        ...state.allMFieldsState,
        [mFieldName]: {
          ...state.allMFieldsState[mFieldName],
          isValid: false,
          touched: true,
        },
      },
    }));
  };

  getMFields = (): MFieldValuePairs =>
    this.getActiveMFieldsNames().reduce((acc, name) => {
      acc[name] =
        this.props.mandatoryFields?.[name] ||
        this.state.allMFieldsState[name]?.value ||
        '';
      return acc;
    }, {});

  getMissingUIMFieldsMeta = (): UIMFieldMeta[] =>
    this.getActiveUIMFieldsMeta().filter(
      ({ name }) => !this.isMFieldProvidedAndValid(name)
    );

  updateAddress = (address: Address) => {
    const { allMFieldsState } = this.state;

    const updateAutofilledField = (fieldName: MFieldName, value = '') => {
      this.updateFieldState(fieldName, {
        value,
        isValid: !!value,
        touched: !!value,
      });
    };

    const fieldsToUpdate = {
      city: address?.city,
      state: address?.state,
      address: address?.address,
      street: address?.street,
      houseNumber: address?.houseNumber,
    };

    getBiLogger().checkoutZipAutoFilledFields({
      value: Object.keys(fieldsToUpdate)
        .filter((key) => allMFieldsState[key] && fieldsToUpdate[key])
        .join(','),
    });

    ['city', 'state', 'address', 'street', 'houseNumber'].forEach(
      (mFieldName: MFieldName) => {
        if (allMFieldsState[mFieldName]) {
          updateAutofilledField(mFieldName, fieldsToUpdate[mFieldName]);
        }
      }
    );
  };

  autofillAddress = ({ zipCode }: { zipCode: string }) => {
    const requestId: string = generateGUID();
    this.activeFetchAddressRequestId = requestId;

    this.delayedFetchAddress({
      countryCode: alpha3ToAlpha2(this.state.countryCode),
      zipCode,
      languageCode: this.props.locale,
      requestId,
    });
  };

  delayedFetchAddress = debounce(
    async ({ countryCode, zipCode, languageCode, requestId }) => {
      let address: Address;

      try {
        address = await new MetadataService({
          baseURL: patchMetadataServiceBaseURL(this.props.host),
        }).fetchAddressV2({
          countryCode,
          zipCode,
          languageCode,
        });
      } catch (e) {
        // do nothing, address won't be updated
      } finally {
        if (requestId !== this.activeFetchAddressRequestId) {
          // tslint:disable-next-line: no-unsafe-finally
          return;
        }
      }

      if (address) {
        this.updateAddress(address);
      }
      this.activeFetchAddressRequestId = null;
    },
    DEBOUNCE_TIME
  );

  isAddressAutofillEnabled = () => {
    const zipCodeAutofillSupportedCountries = ['JPN', 'BRA'];
    return zipCodeAutofillSupportedCountries.includes(this.state.countryCode);
  };

  handleMFieldChange = (
    mFieldName: MFieldName,
    value: string,
    isValid: boolean
  ): void => {
    this.updateFieldState(mFieldName, { value, isValid });

    if (mFieldName === MFieldType.CountryCode) {
      this.props.onCountryChange(value);
    }

    if (
      mFieldName === MFieldType.ZipCode &&
      !!value &&
      this.isAddressAutofillEnabled() &&
      this.state.allMFieldsState[MFieldType.ZipCode].value !== value
    ) {
      this.autofillAddress({ zipCode: value });
    }
  };

  render() {
    return (
      <PaymentDetailsView
        locale={this.props.locale}
        theme={this.props.theme}
        host={this.props.host}
        deviceType={this.props.deviceType}
        shouldApplySiteStyles={this.props.shouldApplySiteStyles}
        isCollapsed={this.props.isCollapsed}
        paymentMethod={this.props.paymentMethod}
        countryCode={this.state.countryCode}
        fieldRef={(mFieldName, fieldElement) =>
          (this.fieldsRefs[mFieldName] = fieldElement)
        }
        allMFieldsState={this.state.allMFieldsState}
        uiMFieldsMeta={this.getMissingUIMFieldsMeta()}
        onChange={this.handleMFieldChange}
      />
    );
  }
}
